import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import type { OnInit } from '@angular/core';
import { Component, inject, Injector, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalComponent,
  OnyxRadioButtonComponent,
  OnyxToastService,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { Subject, switchMap } from 'rxjs';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesNamesTranslationFormComponent } from '../../../common/components/forms/dictionaries-names-translation-form/dictionaries-names-translation-form.component';
import { CompanyDictionaryCode } from '../../../common/enums/company-dictionary-code';
import { DictionariesHelper } from '../../../common/helpers/dictionaries.helper';
import type { CompanyDriverPermission } from '../../../common/interfaces/company-driver-permission';
import { CompanyDictionariesService } from '../../../common/services/company-dictionaries.service';

export type DictionariesDriverPermissionForm = ReturnType<
  ReturnType<
    DictionariesDriverPermissionModalComponent['buildForm']
  >['getRawValue']
>;

export type DictionariesDriverPermissionModalData =
  | {
      mode: OnyxFormMode.ADD;
      name?: string;
    }
  | {
      mode: OnyxFormMode.EDIT;
      permission: CompanyDriverPermission;
    };

@Component({
  selector: 'app-dictionaries-driver-permission-modal',
  imports: [
    OnyxModalComponent,
    OnyxButtonComponent,
    ReactiveFormsModule,
    DictionariesNamesTranslationFormComponent,
    TranslatePipe,
    OnyxRadioButtonComponent,
    OnyxTooltipDirective,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './dictionaries-driver-permission-modal.component.html',
  styleUrl: './dictionaries-driver-permission-modal.component.scss',
})
export class DictionariesDriverPermissionModalComponent implements OnInit {
  protected readonly data =
    inject<DictionariesDriverPermissionModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly dictionariesHelper = inject(DictionariesHelper);

  protected readonly I18N = 'dictionaries';

  protected readonly OnyxFormMode = OnyxFormMode;

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  public ngOnInit(): void {
    if (this.data.mode === OnyxFormMode.EDIT) {
      const { permission } = this.data;
      this.form.patchValue(permission);

      if (!permission.requiredExpirationDate && permission.hasRelatedObjects) {
        ValidationHelper.disableControls(
          this.form.controls.requiredExpirationDate,
          { reset: false },
        );
      }
    }
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();

    let action$;
    switch (this.data.mode) {
      case OnyxFormMode.ADD:
        action$ = this.companyDictionariesService.addDictionaryItem(
          CompanyDictionaryCode.DRIVER_PERMISSION,
          form,
        );
        break;

      case OnyxFormMode.EDIT:
        {
          const { permission } = this.data;
          action$ = this.dictionariesHelper
            .confirmEdit(
              CompanyDictionaryCode.DRIVER_PERMISSION,
              permission,
              this.close$,
            )
            .pipe(
              switchMap(() =>
                this.companyDictionariesService.editDictionaryItem(
                  permission.uuid,
                  form,
                ),
              ),
            );
        }
        break;
    }

    this.loading.set(true);
    action$
      .subscribe({
        next: () => {
          this.toastService.showSuccess({
            message:
              this.data.mode === OnyxFormMode.ADD
                ? `${this.I18N}.dictionaryAdded`
                : `${this.I18N}.dictionaryEdited`,
          });
          this.close$.next();
        },
        error: (response) =>
          this.dictionariesHelper.handleEditError(
            response,
            CompanyDictionaryCode.DRIVER_PERMISSION,
            form,
          ),
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      names: DictionariesNamesTranslationFormComponent.buildFom(this.fb),
      requiredExpirationDate: this.fb.control(false, [Validators.required]),
    });
  }
}
